import { useEffect, useRef, useState } from "react";
import type { Match } from "@/app/(app)/screening/_data/types";
import type { Result } from "@/src/services/types";

export type MatchesPage = { matches: Match[]; count: number; next: string | null };
export type FetchMatchesPage = (page: number, signal?: AbortSignal) => Promise<Result<MatchesPage>>;

type State = {
  matches: Match[];
  count: number;
  next: string | null;
  loadedPages: number;
  isLoading: boolean;
  isLoadingMore: boolean;
  error: string | null;
};

const EMPTY_STATE: State = {
  matches: [],
  count: 0,
  next: null,
  loadedPages: 0,
  isLoading: false,
  isLoadingMore: false,
  error: null,
};

/**
 * Accumulates server pages (page_size=100 each, set by the caller's
 * `fetchPage`) into one growing `matches` array, fetching lazily as the
 * client-side pager (`page`/`rowsPerPage`) needs rows beyond what's already
 * loaded. Refetches page 1 from scratch whenever `resetKey` changes (new
 * search, new tab, new row).
 *
 * Pass `fetchPage: null` when a collection doesn't apply at all (e.g.
 * Adverse Media wasn't part of the search) — the hook then reports an empty,
 * non-loading result and never calls the network.
 */
export function usePaginatedMatches(
  fetchPage: FetchMatchesPage | null,
  resetKey: unknown,
  page: number,
  rowsPerPage: number,
) {
  const [state, setState] = useState<State>(() => ({
    ...EMPTY_STATE,
    isLoading: fetchPage !== null,
  }));
  const fetchPageRef = useRef(fetchPage);
  useEffect(() => {
    fetchPageRef.current = fetchPage;
  });

  // Full reset + page-1 fetch whenever the caller says the underlying query changed.
  useEffect(() => {
    if (!fetchPage) {
      setState(EMPTY_STATE);
      return;
    }
    let cancelled = false;
    const controller = new AbortController();
    setState({ ...EMPTY_STATE, isLoading: true });
    fetchPage(1, controller.signal).then((res) => {
      if (cancelled) return;
      setState(
        res.ok
          ? {
              matches: res.value.matches,
              count: res.value.count,
              next: res.value.next,
              loadedPages: 1,
              isLoading: false,
              isLoadingMore: false,
              error: null,
            }
          : { ...EMPTY_STATE, isLoading: false, error: res.error.message },
      );
    });
    return () => {
      cancelled = true;
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey]);

  // Top up with one more page whenever the client pager's current window runs
  // past what's loaded. This effect only flips a pure state transition; the
  // one below performs the actual fetch — keeps the setState updater pure.
  useEffect(() => {
    const neededIndex = page * rowsPerPage - 1;
    if (state.next === null || state.isLoadingMore || neededIndex < state.matches.length) return;
    setState((s) => (s.isLoadingMore || s.next === null ? s : { ...s, isLoadingMore: true }));
  }, [page, rowsPerPage, state.matches.length, state.next, state.isLoadingMore]);

  useEffect(() => {
    if (!state.isLoadingMore) return;
    const fp = fetchPageRef.current;
    if (!fp) return;
    let cancelled = false;
    const controller = new AbortController();
    const nextPageNum = state.loadedPages + 1;
    fp(nextPageNum, controller.signal).then((res) => {
      if (cancelled) return;
      setState((s) =>
        res.ok
          ? {
              matches: [...s.matches, ...res.value.matches],
              count: res.value.count,
              next: res.value.next,
              loadedPages: nextPageNum,
              isLoading: false,
              isLoadingMore: false,
              error: null,
            }
          : // Stop offering more pages on error instead of retrying forever —
            // `next: null` makes the top-up effect above a no-op until reset.
            { ...s, isLoadingMore: false, error: res.error.message, next: null },
      );
    });
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [state.isLoadingMore, state.loadedPages]);

  return {
    matches: state.matches,
    count: state.count,
    isLoading: state.isLoading,
    isLoadingMore: state.isLoadingMore,
    error: state.error,
  };
}
